import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import { Box, Skeleton, Typography } from '@mui/material';
import { AppCard } from '../../../components/common/AppCard';
import { Figure } from '../../../components/common/page/Figure';
import { usePiprChart } from '../api/usePiprChart';
import {
    countdownDays,
    deltaCompact,
    deltaLabel,
    localDate,
    pct,
    pctInt,
    tsDate,
} from '../chart/format';
import { directionColor, useMonitorPalette } from '../chart/palette';
import { toneColor } from '../pipr/layers/decisionTone';
import { monitorPath } from '../pipr/links';
import { S } from '../pipr/strings';
import { indicatorName, type BankInfo, type PiprBundle } from '../pipr/types';
import { cardFigures } from './cardFigures';

const SPARK_W = 260;
const SPARK_H = 48;

interface PiprCardProps {
    bank: string;
    info: BankInfo | null;
    from: Date;
    to: Date;
}

function Sparkline({ data }: { data: PiprBundle }) {
    const palette = useMonitorPalette();
    const paths = useMemo(() => {
        const h = data.history;
        if (h.n < 2) return null;
        const values = [...h.impliedBp, ...h.anchorBp];
        const x = d3
            .scaleLinear()
            .domain([h.ts[0], h.ts[h.n - 1]])
            .range([1, SPARK_W - 1]);
        const lo = d3.min(values) as number;
        const hi = d3.max(values) as number;
        const pad = Math.max(2, (hi - lo) * 0.15);
        const y = d3
            .scaleLinear()
            .domain([lo - pad, hi + pad])
            .range([SPARK_H - 2, 2]);
        const idx = d3.range(h.n);
        return {
            implied: d3
                .line<number>()
                .x(i => x(h.ts[i]))
                .y(i => y(h.impliedBp[i]))(idx),
            anchor: d3
                .line<number>()
                .x(i => x(h.ts[i]))
                .y(i => y(h.anchorBp[i]))
                .curve(d3.curveStepAfter)(idx),
        };
    }, [data]);
    if (!paths) return null;
    return (
        <svg
            viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
            preserveAspectRatio="none"
            width="100%"
            height={SPARK_H}
            aria-hidden
        >
            <path
                d={paths.anchor ?? ''}
                fill="none"
                stroke={palette.ink}
                strokeOpacity={0.55}
                strokeWidth={1.2}
                strokeDasharray="4 2"
                vectorEffect="non-scaling-stroke"
            />
            <path
                d={paths.implied ?? ''}
                fill="none"
                stroke={palette.ink}
                strokeWidth={1.8}
                vectorEffect="non-scaling-stroke"
            />
        </svg>
    );
}

export function PiprCard({ bank, info, from, to }: PiprCardProps) {
    const navigate = useNavigate();
    const palette = useMonitorPalette();
    const request = useMemo(
        () => ({ bank, from, to, resolution: 1440 }),
        [bank, from, to]
    );
    const { data, error } = usePiprChart(request);
    const fig = useMemo(() => (data ? cardFigures(data) : null), [data]);

    return (
        <AppCard onClick={() => navigate(monitorPath(bank))} sx={{ p: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    mb: 0.25,
                }}
            >
                <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>
                    {indicatorName(bank)}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {info ? `${info.name} · ${info.rateName}` : ''}
                </Typography>
            </Box>

            {error && !data && (
                <Typography
                    sx={{ fontSize: '12px', color: 'text.secondary', py: 3 }}
                >
                    {S.errNetwork}
                </Typography>
            )}
            {!data && !error && <Skeleton variant="rounded" height={140} />}

            {data && fig && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: '2rem',
                                    fontWeight: 500,
                                    lineHeight: 1,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {fig.rateBp !== null ? pct(fig.rateBp, 2) : '–'}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    mt: 0.5,
                                }}
                            >
                                {fig.impliedForTs !== null
                                    ? `${S.impliedFor} ${localDate(tsDate(fig.impliedForTs))}`
                                    : ' '}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Sparkline data={data} />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                            columnGap: 2,
                            mt: 1.5,
                        }}
                    >
                        <Figure
                            label={`Change, ${fig.changeDays} days`}
                            value={
                                fig.changeBp !== null
                                    ? deltaCompact(fig.changeBp)
                                    : '–'
                            }
                            color={
                                fig.changeBp !== null
                                    ? directionColor(fig.changeBp)
                                    : undefined
                            }
                        />
                        <Figure
                            label={S.nextMeeting}
                            value={
                                fig.nextDecisionTs !== null
                                    ? localDate(tsDate(fig.nextDecisionTs))
                                    : '–'
                            }
                            sub={
                                fig.nextDecisionTs !== null
                                    ? countdownDays(
                                          fig.asofTs,
                                          fig.nextDecisionTs
                                      )
                                    : undefined
                            }
                        />
                        <Figure
                            label={S.decisionLikely}
                            value={
                                fig.modalDeltaBp === null
                                    ? '–'
                                    : fig.modalDeltaBp === 0
                                      ? S.hold
                                      : `${fig.modalDeltaBp > 0 ? S.hike : S.cut} ${deltaLabel(fig.modalDeltaBp)}`
                            }
                            sub={
                                fig.modalPermille !== null
                                    ? `${pctInt(fig.modalPermille)} ${S.probability}`
                                    : undefined
                            }
                            color={
                                fig.modalDeltaBp !== null
                                    ? toneColor(fig.modalDeltaBp, palette.ink)
                                    : undefined
                            }
                        />
                    </Box>
                </>
            )}
        </AppCard>
    );
}
